import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dropdown from 'react-bootstrap/Dropdown';
import PropTypes from 'prop-types';
import '../../../../assignments/assignments.scss';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import CustomToast from '../../../../../components/Toast';
import { useParams } from 'react-router';
import { fixSampleAnswerContent, getMcCorrectAnswer } from '../utils/StandardPrintFunctions';

const StandardPrintingContent = ({
  updateSettings,
  assignmentName,
  fontStyleData,
  fontSizeData,
  questionDetails,
  mcSpacingData,
  mcStylingData,
  teacherNameData,
  answerKey,
  dueDate,
  initialInstructions,
  settingsData,
  gotoId,
  setGotoId,
  selectedAnswerOptions,
  isLoading,
  isTwoColumnStack,
  setIsTwoColumnStack,
  questionSettings,
  setContentHeights,
  hideDuplicate,
  updateQuestionDetailsWithQuestionLayout,
  setGotoThumbnailId,
  handleResetImages,
  updateQuestionDetailsOnDrag,
  fixContentAlignment,
  autoScroll,
  standardPageHeight
}) => {
  const [showHeaderOptions, setShowHeaderOptions] = useState(false);
  const [showName, setShowName] = useState(true);
  const [showClass, setShowClass] = useState(true);
  const [showAssignment, setShowAssignment] = useState(true);
  const [showTeacher, setShowTeacher] = useState(true);
  const [fontStyleClass, setFontStyleClass] = useState('');
  const [MCSpacingClass, setMCSpacingClass] = useState('');
  const [MCStylingClass, setMCStylingClass] = useState('');
  const [answerKeyClass, setAnswerKeyClass] = useState('');
  const [teacherNameValue, setTeacherNameValue] = useState('');
  const [dueDateValue, setDueDateValue] = useState('');
  const [instructionsValue, setInstructionsValue] = useState('');
  const questionsContainerRef = useRef(null);
  const [duplicateStacks, setDuplicateStacks] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [isRestrictedDragArea, setIsRestrictedDragArea] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedQuestionId, setDraggedQuestionId] = useState(null);
  const headerDropdownRef = useRef(null);
  const duplicateDivRef = useRef(null);
  const questionContainerRef = useRef(null);
  const draggedElementRef = useRef(null);
  const dragPreviewRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [stackWarnings, setStackWarnings] = useState({});
  const { isAssessment } = useParams();

  const [headerOptions, setHeaderOptions] = useState([
    {
      id: 'hideName',
      name: 'Name',
      toggleName: 'Show Name'
    },
    {
      id: 'hideClass',
      name: 'Class',
      toggleName: 'Show Class'
    },
    {
      id: 'hideAssignment',
      name: isAssessment ? 'Assessment' : 'Assignment',
      toggleName: `Show ${isAssessment ? 'Assessment' : 'Assignment'}`
    },
    {
      id: 'hideTeacher',
      name: 'Teacher',
      toggleName: 'Show Teacher'
    }
  ]);

  const questionLayoutOptions = [
    {
      id: 'answerAbove',
      name: 'Answer Space Above'
    },
    {
      id: 'mcToggleOptions',
      name: 'MC Toggle Options'
    },
    {
      id: 'answerBelow',
      name: 'Answer Space Below'
    },
    {
      id: 'stackLeft',
      name: 'Stack Left (2 column)'
    },
    {
      id: 'pageBreakAbove',
      name: 'Page Break Above'
    },
    {
      id: 'stackRight',
      name: 'Stack Right (2 column)'
    },
    {
      id: 'pageBreakBelow',
      name: 'Page Break Below'
    },
    {
      id: 'exclude',
      name: 'Exclude'
    },
    {
      id: 'ruleLine',
      name: 'Add Rule Line'
    },
    {
      id: 'resetImages',
      name: 'Reset Images'
    },
    {
      id: 'workspace',
      name: 'Add Workspace'
    },
    {
      id: 'gotoThump',
      name: 'Go To Thumb'
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerDropdownRef.current && !headerDropdownRef.current.contains(event.target)) {
        setShowHeaderOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleHeaderOptions = () => {
    setShowHeaderOptions(!showHeaderOptions);
  };

  const handleMenuItem = (questionOptionId, questionId, questionType) => {
    switch (questionOptionId) {
    case 'answerAbove':
    case 'answerBelow':
    case 'pageBreakAbove':
    case 'pageBreakBelow':
    case 'ruleLine':
    case 'workspace':
    case 'mcToggleOptions':
    case 'stackLeft':
    case 'stackRight':
    case 'exclude':
      updateQuestionDetailsWithQuestionLayout(questionOptionId, questionId, questionType, false);
      break;
    case 'removeAnswerAbove':
    case 'removeAnswerBelow':
    case 'removePageBreakAbove':
    case 'removePageBreakBelow':
    case 'removeRuleLine':
    case 'removeWorkspace':
    case 'unstackLeft':
    case 'unstackRight':
    case 'include':
      updateQuestionDetailsWithQuestionLayout(questionOptionId, questionId, questionType, true);
      break;
    case 'gotoThump':
      setGotoThumbnailId(questionId);
      break;
    case 'resetImages':
      handleResetImages(questionId);
      break;
    }

    setSelectedQuestionId(null);
  };

  const handleHeaderMenuItem = (id) => {
    switch (id) {
    case 'hideName':
      setShowName(!showName);
      updateSettings({ showName: !showName });
      break;
    case 'hideClass':
      setShowClass(!showClass);
      updateSettings({ showClass: !showClass });
      break;
    case 'hideAssignment':
      setShowAssignment(!showAssignment);
      updateSettings({ showAssignment: !showAssignment });
      break;
    case 'hideTeacher':
      setShowTeacher(!showTeacher);
      updateSettings({ showTeacher: !showTeacher });
      break;
    }
    setHeaderOptions((prevOptions) =>
      prevOptions.map((item) => (item.id === id ? { ...item, visible: !item.visible } : item))
    );
    setShowHeaderOptions(false);
  };

  const handleFontStyle = () => {
    switch (fontStyleData) {
    case 'Times New Roman':
      setFontStyleClass('times');
      break;
    case 'arial':
      setFontStyleClass('arial');
      break;
    case 'comic Sans MS':
      setFontStyleClass('comic-sans');
      break;
    case 'Courier':
      setFontStyleClass('courier');
      break;
    case 'Tahoma':
      setFontStyleClass('tahoma');
      break;
    case 'Verdana':
      setFontStyleClass('verdana');
      break;
    default:
      setFontStyleClass('times');
      break;
    }
  };

  const handleMCSpacing = () => {
    switch (mcSpacingData) {
    case 1:
      setMCSpacingClass('full-space');
      break;
    case 15:
      setMCSpacingClass('one-half-space');
      break;
    case 2:
      setMCSpacingClass('double-space');
      break;
    default:
      setMCSpacingClass('full-space');
      break;
    }
  };

  const handleMCStyling = () => {
    switch (mcStylingData) {
    case 0:
      setMCStylingClass('decimal');
      break;
    case 1:
      setMCStylingClass('lower');
      break;
    case 2:
      setMCStylingClass('upper');
      break;
    case 3:
      setMCStylingClass('decimal-dot');
      break;
    case 4:
      setMCStylingClass('lower-dot');
      break;
    case 5:
      setMCStylingClass('upper-dot');
      break;
    case 6:
      setMCStylingClass('upper-enclosed');
      break;
    default:
      setMCStylingClass('decimal');
      break;
    }
  };

  const handleAnswerKey = () => {
    switch (answerKey) {
    case 0:
      setAnswerKeyClass('d-none');
      break;
    case 1:
      setAnswerKeyClass('d-flex flex-wrap');
      break;
    case 2:
      setAnswerKeyClass('d-block');
      break;
    default:
      setAnswerKeyClass('d-none');
      break;
    }
  };

  const handleTeacherEdit = (event) => {
    setTeacherNameValue(event.target.value);
    updateSettings({ teacherName: event.target.value });
  };

  const handleDueDateEdit = (event) => {
    setDueDateValue(event.target.value);
    updateSettings({ dueDate: event.target.value });
  };

  const handleInstructionsEdit = (event) => {
    setInstructionsValue(event.target.value);
    updateSettings({ instructions: event.target.value });
  };

  useEffect(() => {
    handleFontStyle();
  }, [fontStyleData]);

  useEffect(() => {
    handleMCSpacing();
  }, [mcSpacingData]);

  useEffect(() => {
    handleMCStyling();
  }, [mcStylingData]);

  useEffect(() => {
    handleAnswerKey();
  }, [answerKey]);

  useEffect(() => {
    setTeacherNameValue(teacherNameData);
    setInstructionsValue(initialInstructions);
    setDueDateValue(dueDate);
    setShowName(settingsData.showName);
    setShowClass(settingsData.showClass);
    setShowAssignment(settingsData.showAssignment);
    setShowTeacher(settingsData.showTeacher);
  }, [teacherNameData, initialInstructions, dueDate, settingsData]);

  useEffect(() => {
    setHeaderOptions((iniOptions) =>
      iniOptions.map((item) => {
        if (item.id === 'hideName') {
          return { ...item, visible: settingsData.showName };
        } else if (item.id === 'hideClass') {
          return { ...item, visible: settingsData.showClass };
        } else if (item.id === 'hideAssignment') {
          return { ...item, visible: settingsData.showAssignment };
        } else if (item.id === 'hideTeacher') {
          return { ...item, visible: settingsData.showTeacher };
        } else {
          return item;
        }
      })
    );
  }, [settingsData]);

  useEffect(() => {
    if (gotoId !== null) {

      let selector;
      if (isTwoColumnStack) {
        selector = `#stack_${gotoId}`;
      } else {
        const isQuestionFound = questionsContainerRef.current.querySelector(`#question_${gotoId}`);
        if (isQuestionFound) {
          selector = `#question_${gotoId}`;
        } else {
          selector = `#stack_${gotoId}`;
        }
      }

      const questionElement = questionsContainerRef.current.querySelector(selector);

      if (questionElement) {
        questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setGotoId(null);
      }
    }
  }, [gotoId]);

  const generateQuestion = (item) => {
    let newQuestionText = item.question.question;
    if (item.type == 'FillIn') {
      var answers = item.question.fillInAnswers;
      if (answers) {
        answers.forEach((item, index) => {
          newQuestionText = newQuestionText.replace(`~~${index + 1}~~`, '<span class=\'answer-box\'></span>');
        });
      }
    }

    return fixContentAlignment(newQuestionText, item.typeId);
  };

  useEffect(() => {
    if (questionDetails.length === 0) return;

    let singleStack = questionDetails.map((question, index) => ({
      ...question,
      questionNo: index + 1
    }));

    setDuplicateStacks(singleStack);

    const hasAtLeastOnePosition = questionSettings?.some(qs =>
      qs.settings?.some(s => s.name === 'sl' || s.name === 'sr')
    );

    const allHavePositions = questionSettings?.length > 0 && questionSettings.every(qs =>
      qs.settings?.some(s => s.name === 'sl' || s.name === 'sr')
    );

    if (hasAtLeastOnePosition && allHavePositions) {
      setIsTwoColumnStack(true);
    }
  }, [questionSettings, questionDetails]);

  useEffect(() => {
    if (duplicateStacks.length > 0 && duplicateDivRef.current) {
      const heights = questionDetails.map(qs => {
        const question = duplicateDivRef.current.querySelector(`#dstack_questionId_${qs.questionId}`);
        if (!question) return { questionId: qs.questionId, height: 0 };

        return {
          questionId: qs.questionId,
          height: question.offsetHeight
        };
      });

      var header = questionsContainerRef.current.querySelector('#documentHeader');
      heights.push({
        questionId: 'header',
        height: header.offsetHeight + 50
      });
      recalculateStackHeights();
      setContentHeights(heights);
    }
  }, [duplicateStacks]);

  useLayoutEffect(() => {
    if (!isTwoColumnStack) return;
    recalculateStackHeights();
  }, [questionDetails]);

  const recalculateStackHeights = () => {
    const newHeights = {};
    document.querySelectorAll('.stack-columns').forEach((stack) => {
      const id = stack.id?.replace('stack_', '');
      if (id) {
        newHeights[id] = stack.offsetHeight;
      }
    });
    compareStackHeights(newHeights);
  };

  const compareStackHeights = (newHeights) => {
    const warnings = {};
    Object.keys(newHeights).forEach((stackNo) => {
      const newHeight = newHeights[stackNo];
      warnings[stackNo] = newHeight > (standardPageHeight + 20);
    });
    setStackWarnings(warnings);
  };

  const RenderQuestion = (item, questionNo, isDuplicate = false) => {
    let questionId = item.questionId;
    let questionType = item.type;
    let isStackEnabled = item.stackNo != null;
    let isAdjQuestionContainer = !(item.type === 'MC' && [1, 3].some(opt => selectedAnswerOptions.includes(opt))) && !item.layoutOptions?.includes('answerAbove');

    return (
      <div className={`question-container ${item.layoutOptions?.includes('exclude') ? 'no-print' : ''}`}>
        {item.layoutOptions?.includes('pageBreakAbove') &&
          <>
            <div className='page-break no-print'></div>
            <div className='page-break-before'></div>
          </>
        }
        <div className={`question-layout text-black ${isStackEnabled ? 'p-0 zoom-9' : ''}`}
          key={`question-${questionNo}${isDuplicate ? '-duplicate' : ''}`} style={{color: 'black'}}
          id={`question-${questionId}${isDuplicate ? '-duplicate' : ''}`}>
          <div id={'q' + questionNo} className={`question-body ${isAdjQuestionContainer ? 'adjQuestion' : ''}`}>
            <div className='d-flex justify-content-between gap-3'>
              <div className={`d-flex ${item.layoutOptions?.includes('exclude') ? 'bg-castleGray mb-2' : ''}`}>
                <>
                  {item.layoutOptions?.includes('exclude') ? <div className='fw-semibold'>Excluded</div> : <div className='fw-semibold'>{item.questionNo}.&nbsp;&nbsp;</div>}
                </>
                {item.type === 'MC' && (
                  selectedAnswerOptions.includes(1) ? (
                    <div className='fw-semibold'>
                    Answer <span className='text-decoration-underline mx-2'>{getMcCorrectAnswer(item.question?.mcAnswer)}</span>
                    </div>
                  ) : selectedAnswerOptions.includes(3) ? (
                    <div className='fw-semibold'>Answer _______ </div>
                  ) : null
                )}

                {item.layoutOptions?.includes('answerAbove') && !(item.type === 'MC' && (selectedAnswerOptions.includes(1) || selectedAnswerOptions.includes(3))) &&
                <div className='fw-semibold'>Answer _______ </div>
                }
              </div>
              <div className="mega-dropdown-btn no-change col-lg-3 d-flex justify-content-end no-print" id="mega-dropdown-button">
                <div className="expand d-flex align-items-center">
                  <Dropdown id={`layoutDropdown-${item.questionId}`}
                    show={selectedQuestionId === item.questionId}
                    onToggle={(isOpen) => {
                      setSelectedQuestionId(isOpen ? item.questionId : null);
                    }}>
                    <Dropdown.Toggle id="toggleLayout">
                      <div className='cursor-pointer'>
                        <span>Layout</span>
                        <FontAwesomeIcon icon={selectedQuestionId == item.questionId ? faMinus : faPlus} className="expand-icon" />
                      </div>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="more-dropdown p-3">
                      {getQuestionLayoutOptions(item.layoutOptions).map((item, index) => (
                        <Dropdown.Item
                          key={`layout${index}`}
                          className='fs-14'
                          onClick={() => {
                            handleMenuItem(item.id, questionId, questionType);
                          }}>
                          {item.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>
            <div className='disable-Pointer-Events'>
              {item.question?.resources?.map((resource, index) => (
                <div key={index} dangerouslySetInnerHTML={{ __html: fixContentAlignment(resource, item.typeId) }}></div>
              ))}
              <div>
                {item.question != null && (
                  <div dangerouslySetInnerHTML={{ __html: generateQuestion(item) }}></div>
                )}
                {item.blockedText?.length > 0 && (
                  <div
                    className="fw-bold text-danger"
                    dangerouslySetInnerHTML={{ __html: item.blockedText }}></div>
                )}
              </div>
              {item.type === 'MC' && item.question.mcAnswer.filter(a => a.answer.trim() !== '').length > 1 && (
                <div className="mcAnswer-content">
                  <ul className={`${MCSpacingClass} ${MCStylingClass} ${item.mcToggleOption === 1 ? 'horizontal-list' : item.mcToggleOption === 2 ? 'two-column-list' : ''}`}>
                    {item.question.mcAnswer.map((answer, indexValue) => (
                      <li
                        key={`ans${indexValue}`}
                        dangerouslySetInnerHTML={{
                          __html: fixContentAlignment(answer.answer.replace(/<p>/g, '').replace(/<\/p>/g, ''), item.typeId)
                        }}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='disable-Pointer-Events'>
          {((item.type == 'CR' && selectedAnswerOptions.includes(2)) || (item.type == 'FillIn' && selectedAnswerOptions.includes(4))) && !selectedAnswerOptions.includes(1) &&
          <>
            <div className='fw-semibold'>Answer</div>
            <div className='h-70 border border-secondary'></div>
          </>
          }

          {(item.type == 'CR' || item.type == 'FillIn') && selectedAnswerOptions.includes(1) &&
          <>
            <div className='fw-semibold'>Answer</div>
            <div className='border border-secondary p-3'
              dangerouslySetInnerHTML={{
                __html: item.type == 'CR' ?
                  (item.question?.sampleAnswer ? fixContentAlignment(fixSampleAnswerContent(item.question?.sampleAnswer), item.typeId) : 'There is no Sample Answer for this Contructed Response Question')
                  : fixContentAlignment(item.question?.fillInAnswers?.join(', '), item.typeId)
              }}>
            </div>
          </>
          }

          {item.layoutOptions?.includes('workspace') &&
          <div className='mx-2 fw-semibold'>Workspace
            <div className='h-70 border border-2'></div>
          </div>
          }

          {item.layoutOptions?.includes('answerBelow') &&
          <div className={`mx-2 fw-semibold ${item.layoutOptions?.includes('addWorkSpace') ? 'pt-2' : ''}`}>Answer
            <div className='h-70 border border-2'></div>
          </div>
          }

          {item.layoutOptions?.includes('ruleLine') &&
          <div className='line'></div>
          }

          {item.layoutOptions?.includes('pageBreakBelow') &&
          <>
            <div className='page-break no-print'></div>
            <div className='page-break-after'></div>
          </>
          }
        </div>
      </div>
    );
  };

  const getQuestionLayoutOptions = (layoutOptions = []) => {
    const updatedOptions = [...questionLayoutOptions];

    const replacementMap = {
      answerAbove: { id: 'removeAnswerAbove', name: 'Remove Answer Space' },
      answerBelow: { id: 'removeAnswerBelow', name: 'Remove Answer Space' },
      pageBreakAbove: { id: 'removePageBreakAbove', name: 'Remove Page Break' },
      pageBreakBelow: { id: 'removePageBreakBelow', name: 'Remove Page Break' },
      ruleLine: { id: 'removeRuleLine', name: 'Remove Rule Line' },
      workspace: { id: 'removeWorkspace', name: 'Remove Workspace' },
      stackLeft: { id: 'unstackLeft', name: 'UnStack' },
      stackRight: { id: 'unstackRight', name: 'UnStack' },
      exclude: { id: 'include', name: 'Include' }
    };

    updatedOptions.forEach((option, index) => {
      if (layoutOptions.includes(option.id) && replacementMap[option.id]) {
        updatedOptions[index] = replacementMap[option.id];
      }
    });

    return updatedOptions;
  };

  const onDragStart = (e, question, isFromContent) => {
    isDraggingRef.current = true;
    requestAnimationFrame(() => {
      setIsDragging(isFromContent? true : false);
      setDraggedQuestionId(question?.questionId);
    });
    
    e.dataTransfer.setData('text/plain', question?.questionId);
    e.dataTransfer.effectAllowed = 'move';
    const dragSource = e.currentTarget instanceof HTMLElement ? e.currentTarget : e.target;
    if (dragSource instanceof HTMLElement) {
      const dragPreview = document.createElement('div');
      dragPreview.className = 'drag-preview-chip';
      dragPreview.textContent = `Question ${question?.questionNo ?? ''}`.trim();
      document.body.appendChild(dragPreview);
      dragPreviewRef.current = dragPreview;
      e.dataTransfer.setDragImage(dragPreview, 12, 12);
    }

    draggedElementRef.current = dragSource;
    window.draggedQuestionRef = question; 
  };

  const onDragOver = (e, targetStackNo, targetPosition, targetIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsRestrictedDragArea(false);
    setDragOverTarget({ stackNo: targetStackNo, position: targetPosition, index: targetIndex });
  };

  const onDragLeave = () => {
    // Safari fires dragleave frequently while moving across child nodes.
    // Keep drag state until actual drop/end to avoid apparent drag cancellation.
  };

  const clearDragState = () => {
    isDraggingRef.current = false;
    setIsDragging(false);
    setDraggedQuestionId(null);
    draggedElementRef.current = null;
    window.draggedQuestionRef = null;
    setDragOverTarget(null);
    setIsRestrictedDragArea(false);
    if (dragPreviewRef.current) {
      dragPreviewRef.current.remove();
      dragPreviewRef.current = null;
    }
    document.body.style.cursor = 'default';
  };

  const onDragEnd = () => {
    clearDragState();
  };

  const onDrop = (e, targetStackNo, targetPosition, targetIndex) => {
    e.preventDefault();
    const resolvedDropTarget = typeof targetIndex === 'number'
      ? { stackNo: targetStackNo, position: targetPosition, index: targetIndex }
      : dragOverTarget;

    if (!resolvedDropTarget) return;

    const containerRef = questionContainerRef.current;
    const draggedRef = draggedElementRef.current;

    if (!containerRef || !draggedRef) return;
    const isDraggedInside = containerRef.contains(draggedRef);

    if (!isDraggedInside) {
      CustomToast('You are trying to drop content from outside question container.', 'warning');
      clearDragState();
      return;
    }

    const questionId = e.dataTransfer?.getData('text/plain') || draggedQuestionId;
    if (!questionId) {
      clearDragState();
      return;
    }

    let updatedQuestionData = [...questionDetails];
    const dragIndex = updatedQuestionData.findIndex(
      q => String(q.questionId) === String(questionId)
    );
    if (dragIndex === -1) {
      clearDragState();
      return questionDetails;
    }

    const [movedQuestion] = updatedQuestionData.splice(dragIndex, 1);
    movedQuestion.stackNo = resolvedDropTarget.stackNo;
    movedQuestion.position = resolvedDropTarget.position;

    const stackIndices = updatedQuestionData.map((q, i) => ({ q, i })).filter(({ q }) => q.stackNo === resolvedDropTarget.stackNo);

    const firstStackIndex = stackIndices[0]?.i ?? updatedQuestionData.length;
    const lastStackIndex = stackIndices.at(-1)?.i ?? updatedQuestionData.length;
    const sameSideItems = stackIndices.filter(({ q }) => q.position === resolvedDropTarget.position);

    let insertIndex;
    if (sameSideItems.length === 0) {
      insertIndex = resolvedDropTarget.position === 'left' ? firstStackIndex : lastStackIndex + 1;
    } else if (resolvedDropTarget.index < sameSideItems.length) {
      insertIndex = sameSideItems[resolvedDropTarget.index].i;
    } else {
      insertIndex = sameSideItems.at(-1).i + 1;
    }

    updatedQuestionData.splice(insertIndex, 0, movedQuestion);

    // eslint-disable-next-line no-unused-vars
    const clearedQuestionData = updatedQuestionData.map(({ isEndStack, ...rest }) => rest);
    const updatedQuestionDataWithisEndStack = clearedQuestionData.map((q, i, arr) => {
      const next = arr[i + 1];
      const isEndStack = q.stackNo != null && (!next || next.stackNo == null || next.stackNo !== q.stackNo);

      return { ...q, ...(isEndStack ? { isEndStack: true } : {}) };
    });

    const reorderedQuestionData = updatedQuestionDataWithisEndStack.map((q, i) => {
      const layoutOptions = q.layoutOptions?.filter(opt => !['stackLeft', 'stackRight', 'endStack'].includes(opt)) || [];

      if (q.position === 'left') layoutOptions.push('stackLeft');
      else if (q.position === 'right') layoutOptions.push('stackRight');

      if (q.isEndStack) layoutOptions.push('endStack');

      return { ...q, questionNo: i + 1, layoutOptions };
    });
    recalculateStackHeights();
    updateQuestionDetailsOnDrag(reorderedQuestionData);
    clearDragState();
  };

  const onContainerDragOver = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
  };

  const onContainerDrop = (e) => {
    if (!dragOverTarget) return;

    const currentTarget = e.target instanceof Element ? e.target : null;
    const droppedOnZone = !!currentTarget?.closest('[data-drop-zone="true"]');
    if (droppedOnZone) return;

    onDrop(e);
  };

  const DropZone = (stackNo, position, index, extendHeight = false) => {
    return (isDragging &&
      <div
        key={`drop_${stackNo}_${position}_${index}`}
        data-drop-zone="true"
        data-stack-no={stackNo ?? ''}
        data-position={position}
        data-index={index}
        className={`align-items-center d-flex justify-content-center drop-zone no-print no-select ${extendHeight ? 'h-100' : ''} ${dragOverTarget?.stackNo === stackNo && dragOverTarget?.position === position && dragOverTarget?.index === index ? 'active' : ''}`}
        onDragOver={(e) => onDragOver(e, stackNo, position, index)}
        onDrop={(e) => {
          e.stopPropagation();
          onDrop(e, stackNo, position, index);
        }}
      >
        Drop here
      </div>
    );
  };

  useEffect(() => {
    const handleDragOver = (e) => {
      if (!isDraggingRef.current) return;

      const container = scrollContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;

      if (!inside) {
        setIsRestrictedDragArea(true);
        document.body.style.cursor = 'not-allowed';
        return;
      }

      setIsRestrictedDragArea(false);
      document.body.style.cursor = 'default';
    
      autoScroll(e.clientY, scrollContainerRef);
    };

    document.addEventListener('dragover', handleDragOver);

    return () => {
      document.removeEventListener('dragover', handleDragOver);
      setIsRestrictedDragArea(false);
      document.body.style.cursor = 'default';
    };
  }, [autoScroll]);

  return (
    <>
      {isRestrictedDragArea && (
        <div className="drag-restricted-indicator" role="status" aria-live="polite">
          Cannot drop here. Move over a Drop here area.
        </div>
      )}
      <div className={`print-content-scroll ${isRestrictedDragArea ? 'drop-outside-active' : ''}`}
        ref={scrollContainerRef}
        onDragOver={onContainerDragOver}
        onDrop={onContainerDrop}
      >
        <div id='print-content-container' className='print-content-container'>
          <div>
            {isLoading ? (
              <div className='print-content p-4'>
                <SkeletonGrid
                  rowCount={2}
                  colCount={2}
                  className='mt-2' />
                <SkeletonGrid
                  rowCount={4}
                  colCount={1}
                  className='p-3 h-150 mt-3'
                />
              </div>
            ) : (
              <div id='print-content' className="print-content">
                <div id='paddedContainer'
                  className={`content-print border border-dark ${fontStyleClass} content-print${fontSizeData}`}
                  ref={questionsContainerRef}>
                  <div id='documentHeader'>
                    <div className="header-options-wrapper no-change no-print" id="header-options">
                      <div className="header-options expand d-flex flex-lg-shrink-0 align-items-center">
                        <Dropdown ref={headerDropdownRef}>
                          <Dropdown.Toggle id="toggle">
                            <div
                              className='cursor-pointer'
                              onClick={() => {
                                handleHeaderOptions();
                              }}>
                              <span>Header Options</span>
                              <FontAwesomeIcon icon={showHeaderOptions ? faMinus : faPlus} className="expand-icon" />
                            </div>
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="overflow-hidden">
                            {headerOptions.map((item, index) => (
                              <Dropdown.Item
                                key={`header${index}`}
                                className='fs-14'
                                onClick={() => {
                                  handleHeaderMenuItem(item.id);
                                }}>
                                {`${item.visible ? 'Hide' : 'Show'} ${item.name}`}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                    <div className="name-slip text-black">
                      <div className={`d-flex align-items-center justify-content-between ${showHeaderOptions ? '' : 'cursor-none'}`}>
                        <div>
                          {showName && (
                            <div>
                              <label className="form-label">Name:</label>
                              <input className="input-name" readOnly></input>
                            </div>
                          )}
                          {showAssignment && (
                            <div>
                              <div className="d-flex gap-2 align-items-center">
                                <label>
                                  {isAssessment?'Assessment ':'Assignment '}:{' '}
                                  <span className={`content-print${fontSizeData} fw-bold mx-2`}>
                                    {assignmentName}
                                  </span>
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          {showClass && (
                            <div>
                              <label className="form-label">Class/Period:</label>
                              <input className="input-name" readOnly></input>
                            </div>
                          )}
                          {showTeacher && (
                            <div className="d-flex gap-2 align-items-center">
                              <label>Teacher:</label>
                              <div>
                                <input
                                  className={`form-control content-print${fontSizeData} fw-bold ${showHeaderOptions
                                    ? 'show-box'
                                    : `${teacherNameValue?.length > 0 ? 'teacher-edit' : 'd-none'}`
                                  }`}
                                  value={teacherNameValue?.length > 0 ? teacherNameValue : ''}
                                  onChange={handleTeacherEdit}
                                  readOnly={teacherNameValue?.length > 0 && !showHeaderOptions}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`${showHeaderOptions ? '' : 'cursor-none'}`}>
                        {(showHeaderOptions || dueDateValue?.length > 0) && (
                          <div className="d-flex gap-2 align-items-center my-2">
                            <label className="col-2">{isAssessment ? 'Assessment' : 'Assignment'} Due Date:</label>
                            <input
                              className={`form-control content-print${fontSizeData} fw-bold ${showHeaderOptions
                                ? 'show-box'
                                : `${dueDateValue?.length > 0 ? 'teacher-edit' : 'd-none'}`
                              }`}
                              value={dueDateValue?.length ? dueDateValue : ''}
                              onChange={handleDueDateEdit}
                              readOnly={dueDateValue?.length > 0 && !showHeaderOptions}
                            />
                          </div>
                        )}
                        {(showHeaderOptions || instructionsValue?.length > 0) && (
                          <div className="d-flex gap-2 align-items-center">
                            <label className="col-2">Instructions:</label>
                            <textarea
                              className={`form-control content-print${fontSizeData} fw-bold ${showHeaderOptions
                                ? 'show-box'
                                : `${instructionsValue?.length > 0 ? 'teacher-edit' : 'd-none'}`
                              }`}
                              value={instructionsValue?.length > 0 ? instructionsValue : ''}
                              onChange={handleInstructionsEdit}
                              readOnly={instructionsValue?.length > 0 && !showHeaderOptions}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div id="question-details" className={'mt-3 questions-container'} ref={questionContainerRef}>
                    {(() => {
                      const elementsToRender = [];
                      let leftColumn = [];
                      let rightColumn = [];
                      let currentStackNo = null;

                      questionDetails.forEach((item, index) => {
                        const hasPosition = item.position === 'left' || item.position === 'right';

                        if (hasPosition) {
                          if (!currentStackNo && item.stackNo) {
                            currentStackNo = item.stackNo;
                          }

                          if (item.position === 'left') leftColumn.push(item);
                          else rightColumn.push(item);

                          const next = questionDetails[index + 1];
                          const nextStackNo = next?.stackNo;
                          if (item.isEndStack) {
                            const stackNoForRender = currentStackNo;
                            elementsToRender.push(
                              <div key={`stack_${stackNoForRender ?? index}`} className={`mb-2 ${stackNoForRender > 1 ? 'page-break-before' : ''}`}>
                                {stackNoForRender && (
                                  <div id={`stackname_${stackNoForRender}`} className="no-print">
                                    <p className='d-flex justify-content-center fw-bold line'>Stack</p>
                                  </div>
                                )}
                                <div id={`stack_${stackNoForRender}`} className='stack-columns'>
                                  <div className="stack-column">
                                    {DropZone(stackNoForRender, 'left', 0, leftColumn.length == 0)}
                                    {leftColumn.map((q, i) => (
                                      <React.Fragment key={q.questionId}>
                                        <div
                                          key={q.questionId}
                                          draggable
                                          onDragStart={(e) => onDragStart(e, q, true)}
                                          onDragLeave={onDragLeave}
                                          onDragEnd={onDragEnd}
                                          className={`question-item mb-2 cursor-grab ${draggedQuestionId === q.questionId ? 'dragged' : ''}`}
                                        >
                                          {RenderQuestion(q, q.questionNo)}
                                        </div>
                                        {DropZone(stackNoForRender, 'left', i + 1)}
                                      </React.Fragment>
                                    ))}
                                  </div>
                                  <div className='vertical-line'></div>
                                  <div className="stack-column">
                                    {DropZone(stackNoForRender, 'right', 0, rightColumn.length == 0)}
                                    {rightColumn.map((q, i) => (
                                      <React.Fragment key={q.questionId}>
                                        <div
                                          key={q.questionId}
                                          draggable
                                          onDragStart={(e) => onDragStart(e, q, true)}
                                          onDragLeave={onDragLeave}
                                          onDragEnd={onDragEnd}
                                          className={`question-item mb-2 cursor-grab ${draggedQuestionId === q.questionId ? 'dragged' : ''}`}
                                        >
                                          {RenderQuestion(q, q.questionNo)}
                                        </div>
                                        {DropZone(stackNoForRender, 'right', i + 1)}
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                                {stackWarnings && stackWarnings[stackNoForRender] && (
                                  <div className="stack-warning no-print">
                                    <span>
                                    Stack Exceeds Page Height
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                            leftColumn = [];
                            rightColumn = [];
                            currentStackNo = nextStackNo ?? null;
                          }
                        } else {
                          elementsToRender.push(
                            <div key={item.questionId} id={`question_${item.questionId}`} className="mb-2">
                              {RenderQuestion(item, item.questionNo)}
                            </div>
                          );
                        }
                      });

                      return elementsToRender;
                    })()}
                  </div>

                  {duplicateStacks && duplicateStacks.length > 0 && !hideDuplicate &&
                  <div id='duplicate-question-div' ref={duplicateDivRef}>
                    <div id="dstack" className="d-flex row mb-5">
                      <p className="d-flex justify-content-center fw-bold line">Duplicate Stack</p>
                      <div className="d-flex gap-3">
                        <div style={{ flex: 1 }}>
                          {duplicateStacks.map((item, index) => (
                            <div id={`dstack_questionId_${item.questionId}`} key={`dstack_questionId_${item.questionId ?? index + 1}`} className={`dstack_questionId_${item.questionId ?? index + 1}`}>
                              {RenderQuestion(item, item.questionNo ?? index + 1, true)}
                            </div>
                          ))}
                        </div>
                        <div style={{ flex: 1 }} />
                      </div>
                    </div>
                  </div>
                  }

                  {settingsData && settingsData.answerKey > 0 &&
                  <div className="answerkey-content mt-4 page-break-before">
                    <h4 className={answerKeyClass}>Answer Key For : {assignmentName}</h4>
                    <div className={answerKeyClass}>
                      {questionDetails.map((item, index) => (
                        <div
                          key={`questionDetails${index}`}
                          className={answerKeyClass === 'd-flex flex-wrap' && item.type != 'CR' ? 'answerKey-data mr-2' : ''}>
                          <div className="d-flex gap-2">
                            <div className='mr-2' style={{minWidth:'60px'}}><h5>{`Q${index + 1} :  `}</h5></div>
                            <div>
                              {item.type === 'MC' &&
                                item.question?.mcAnswer?.map((answer, indexValue) => {
                                  if (answer.isCorrect === 1) return indexValue + 1;
                                })}
                              {item.type === 'CR' && (
                                <>
                                  <span>Constructed Response:</span>
                                  <div dangerouslySetInnerHTML={{ __html: fixContentAlignment(fixSampleAnswerContent(item.question?.sampleAnswer), item.typeId) }}></div>
                                </>
                              )}
                              {item.type === 'FillIn' &&
                                  <div dangerouslySetInnerHTML={{ __html: fixContentAlignment(item.question?.fillInAnswers?.join(', '), item.typeId) }}></div>
                              }
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

StandardPrintingContent.propTypes = {
  assignmentName: PropTypes.string,
  fontStyleData: PropTypes.string,
  fontSizeData: PropTypes.number,
  mcSpacingData: PropTypes.number,
  mcStylingData: PropTypes.number,
  questionDetails: PropTypes.array,
  teacherName: PropTypes.string,
  answerKey: PropTypes.number,
  updateSettings: PropTypes.func,
  initialInstructions: PropTypes.string,
  teacherNameData: PropTypes.string,
  dueDate: PropTypes.string,
  settingsData: PropTypes.object,
  gotoId: PropTypes.number,
  setGotoId: PropTypes.func,
  selectedAnswerOptions: PropTypes.array,
  isLoading: PropTypes.bool,
  isTwoColumnStack: PropTypes.bool,
  setIsTwoColumnStack: PropTypes.func,
  questionSettings: PropTypes.array,
  setContentHeights: PropTypes.func,
  hideDuplicate: PropTypes.bool,
  updateQuestionDetailsWithQuestionLayout: PropTypes.func,
  setGotoThumbnailId: PropTypes.func,
  handleResetImages: PropTypes.func,
  updateQuestionDetailsOnDrag: PropTypes.func,
  fixContentAlignment: PropTypes.func,
  autoScroll: PropTypes.func,
  standardPageHeight: PropTypes.number,
};

export default StandardPrintingContent;
